import { FC, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { toastModule } from "@/modules/toast";
import { localStorageModule } from "@/modules/local-storage";
import { logger } from "@/modules/logger";
import { windowModule } from "@/modules/window";
import { clientEnvModule } from "@/modules/client-env";
import { objectModule } from "@/modules/object";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { appRoutes } from "@/app/router/appRoutes";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import { TabOpeningStrategy } from "@/store/navigation/NavigationTabManager";
import {
  getSlackOAuthClientKindFromState,
  SlackOAuthClientKind,
} from "@/domains/integrations/slack-oauth-client-kind";

const getSlackReauthorizationFailureMessage = (error: string | null): string => {
  if (error === "reauthorization_identity_mismatch") {
    return "Slack wasn't reauthorized because a different Slack workspace or account was selected. Your existing Slack connection is unchanged. Try again and choose the currently connected Slack workspace and account.";
  }

  if (error === "reauthorization_stale") {
    return "Your Slack connection changed while reauthorization was in progress. Refresh Mem Agent settings and try again.";
  }

  return "Slack wasn't reauthorized. Your existing Slack connection is unchanged.";
};

export const IntegrationsSlackCallbackPage: FC = observer(() => {
  const { store } = useAppStore();
  const location = useLocation();
  const hasProcessedCallback = useRef(false);

  useLayoutEffect(() => {
    if (hasProcessedCallback.current) {
      return;
    }

    hasProcessedCallback.current = true;

    const params = new URLSearchParams(location.search);
    const success = params.get("success");
    const error = params.get("error");
    const state = params.get("state");

    const storedOAuthContext = localStorageModule.readSlackOAuthContext();
    const storedState = storedOAuthContext?.state ?? null;
    const isReauthorization = storedOAuthContext?.connection_intent === "reauthorize";
    const connectionContext = {
      source: storedOAuthContext?.source ?? "settings",
      is_setup: storedOAuthContext?.is_setup ?? false,
      ...(storedOAuthContext?.original_plan_kind
        ? { original_plan_kind: storedOAuthContext.original_plan_kind }
        : {}),
      ...(storedOAuthContext?.original_effective_plan_kind
        ? { original_effective_plan_kind: storedOAuthContext.original_effective_plan_kind }
        : {}),
    };
    const shouldShowConnectedModalAfterReturn =
      connectionContext.source === "proactive_checkout" && connectionContext.is_setup;
    const returnedStateClientKind = getSlackOAuthClientKindFromState(state);
    const isNativeAppReturnedToWeb =
      returnedStateClientKind === SlackOAuthClientKind.Desktop ||
      returnedStateClientKind === SlackOAuthClientKind.Ios;

    const goToMemAgentSettings = () => {
      store.navigation.tabs.openTab({
        path: `${appRoutes.notesList({}).path}?settings=${SettingsTabId.MemAgentCommunicationChannelsPage}`,
        strategy: TabOpeningStrategy.SAME_TAB,
        config: {
          routerOptions: {
            replace: true,
            // The active tab may already be the target path after returning from Slack OAuth.
            state: { source: "slack-oauth-callback" },
          },
        },
      });
    };

    const goToNotes = () => {
      store.navigation.tabs.openTab({
        path: appRoutes.notesList({}).path,
        strategy: TabOpeningStrategy.SAME_TAB,
        config: {
          routerOptions: {
            replace: true,
            state: { source: "slack-oauth-callback" },
          },
        },
      });
    };

    if (!clientEnvModule.isDesktop() && !storedState && isNativeAppReturnedToWeb) {
      const isIosCallback = returnedStateClientKind === SlackOAuthClientKind.Ios;

      logger.info({
        message: isIosCallback
          ? "[IntegrationsSlackCallbackPage] iOS Slack OAuth state returned to web, redirecting to Mem iOS"
          : "[IntegrationsSlackCallbackPage] Desktop Slack OAuth state returned to web, redirecting to Electron",
        info: { search: location.search },
      });

      const asyncLogic = async () => {
        try {
          const targetUrl = new URL(`mem://integrations/slack/callback${location.search}`);
          await windowModule.navigateToExternal({ url: targetUrl });
        } catch (unknownError) {
          logger.error({
            message: isIosCallback
              ? "[IntegrationsSlackCallbackPage] Failed to redirect to Mem iOS"
              : "[IntegrationsSlackCallbackPage] Failed to redirect to Electron",
            info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
          });
          trackEvent(TrackedEvent.MemAgentSlackConnectFailed, {
            ...connectionContext,
            failure_reason: isIosCallback ? "ios_redirect_failed" : "desktop_redirect_failed",
            error_message:
              unknownError instanceof Error ? unknownError.message : "Unknown redirect error",
          });
          toastModule.triggerToast({
            content: "Unable to complete Slack connection. Please try again.",
          });
        } finally {
          goToMemAgentSettings();
        }
      };

      void asyncLogic();
      return;
    }

    store.memAgent.clearPendingSlackOAuthContext();
    store.subscriptions.closeMemProactiveRedirectModal();

    if (storedState && state && storedState !== state) {
      logger.error({
        message: "[IntegrationsSlackCallbackPage] OAuth state mismatch",
      });
      trackEvent(TrackedEvent.MemAgentSlackConnectFailed, {
        ...connectionContext,
        failure_reason: "state_mismatch",
      });

      toastModule.triggerToast({
        content: isReauthorization
          ? getSlackReauthorizationFailureMessage(null)
          : "Slack connection failed. Please try again.",
      });

      goToMemAgentSettings();
      return;
    }

    if (error || success === "false") {
      logger.error({
        message: "[IntegrationsSlackCallbackPage] Slack OAuth failed",
        info: { error: error ?? "unknown_error" },
      });
      trackEvent(TrackedEvent.MemAgentSlackConnectFailed, {
        ...connectionContext,
        failure_reason: error ? "oauth_error" : "unknown_error",
        error_message: error ?? undefined,
      });

      toastModule.triggerToast({
        content: isReauthorization
          ? getSlackReauthorizationFailureMessage(error)
          : "Failed to connect Slack. Please try again.",
      });

      goToMemAgentSettings();
      return;
    }

    if (!storedState) {
      logger.warn({
        message: "[IntegrationsSlackCallbackPage] Missing OAuth state in localStorage",
      });
    } else if (!state) {
      logger.warn({
        message: "[IntegrationsSlackCallbackPage] Missing OAuth state in callback URL",
      });
    }

    if (success === "true") {
      void store.memAgentCallout.dismissIfPresent();

      if (isReauthorization) {
        toastModule.triggerToast({
          content: "Slack reauthorized successfully.",
        });
        goToMemAgentSettings();
        return;
      }

      if (shouldShowConnectedModalAfterReturn) {
        localStorageModule.writeMemAgentSlackConnectedModalPending(connectionContext);
        goToNotes();
        return;
      }

      goToMemAgentSettings();
      return;
    }

    trackEvent(TrackedEvent.MemAgentSlackConnectFailed, {
      ...connectionContext,
      failure_reason: "unknown_error",
    });
    toastModule.triggerToast({
      content: isReauthorization
        ? getSlackReauthorizationFailureMessage(null)
        : "Slack connection failed. Please try again.",
    });

    goToMemAgentSettings();
  }, [location.search, store]);

  return null;
});
